import { formatCurrency } from '@/helpers/number';

/**
 * Converts 1 dimensional array into string, separated with commas. Empty/Null value will be excluded.
 * @param  {array} value
 * @returns {string}
 */
export function arrayToCommas(values = []) {
  if (values === []) return '';
  return values.filter(x => x !== '' && x !== null).join(',');
}

/**
 * Converts string separated with commas into array. String will be trimmed before being processed.
 * @param  {string} value
 * @returns {array}
 */
export function commasToArray(value = '') {
  if (value === '') return [];
  return value.trim().split(',');
}

/**
 * Append positive integers with its singluar/plural names. eg. 1 apple, 2 apples, no apple.
 * You might need to specify its plural form manually for some words.
 * @param  {integer} value=0
 * @param  {string} singluar=''
 * @param  {string} plural=null
 * @param  {string} emptyPrefix='no'
 * @returns {string}
 */
export function pluralize(value = 0, singluar = '', plural = null, emptyPrefix = 'no') {
  if (value < 0) return '';

  // Use formatCurrency for large numbers (1000+), otherwise use plain value
  const number = value >= 1 ? formatCurrency(value) : emptyPrefix;
  const pluralLabel = plural === null ? `${singluar}s` : plural;

  return `${number} ${value >= 2 ? pluralLabel : singluar}`;
}

/**
 * Set singular or plural form of word eg. apple / apples.
 * You might need to specify its plural form manually for some words.
 * @param  {integer} value=0
 * @param  {string} singluar=''
 * @param  {string} plural=null
 * @returns {string}
 */
export function pluralizeText(value = 0, singluar = '', plural = null) {
  const pluralLabel = plural === null ? `${singluar}s` : plural;

  return `${value >= 2 ? pluralLabel : singluar}`;
}

/**
 * Remove white space in string and separate every word with "-".
 * @param  {string} value
 * @returns {string}
 */
export function makeSlug(val = '', separator = '-') {
  let value = val;
  if (value !== undefined) {
    value = value.trim ? value.trim() : value.replace(/^\s+|\s+$/g, '');
    return value
      .split(/\s+/)
      .join(separator)
      .toLowerCase();
  }
  return '';
}

/**
 * Convert " " to "_" and lower case, eg: "Self review" become self_review.
 * @param  {string} value
 * @returns {string}
 */
export function underscore(value = '') {
  return makeSlug(value, '_');
}

/**
 * Capitalize first letter, eg: "self review" become "Self review".
 * @param  {string} value
 * @returns {string}
 */
export function titleCase(value = '') {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * truncate string after specific count and put ellipsis at the end
 * @param {string} value // input value, i.e. 'Growth Plan'
 * @param {number} maxCount // max count of characters for maxCount = 5, 'Growth' -> 'Gro...'
 * @returns {string} // converted value 'Gro...'
 */

export function truncateEllipsis(value = '', maxCount = 60) {
  const res = value;
  if (res && res.length > maxCount) {
    return `${res.trim().substring(0, maxCount)}...`;
  }

  return res;
}

/**
 * Remove id of file name, eg: "249722270ac1795d63368ea_bukti_transfer.jpeg" become bukti_transfer.jpeg.
 * @param  {string} value
 * @returns {string}
 */
export function originalFileName(value = '') {
  return value?.substring(value?.indexOf('_') + 1) || '';
}

export default { arrayToCommas, commasToArray, pluralize, makeSlug, underscore, truncateEllipsis, originalFileName };
